import { SelectedRoleEnum } from '@/store/types'
import { $t } from 'packages/locale'
import { createElement, Fragment, type ReactNode } from 'react'

/**
 * 获取考勤卡片的提示
 *
 * @param param0 - 参数
 * @param param0.role - 角色
 * @returns 提示
 */
export const getAttendanceCardTips = (role: SelectedRoleEnum | undefined) => {
  switch (role) {
    case SelectedRoleEnum.Mentor:
      return $t('迟到早退≥5次或缺席≥1次的学生')
    case SelectedRoleEnum.Dean:
    case SelectedRoleEnum.Yuke:
    case SelectedRoleEnum.SchoolLeaders:
      return $t('迟到早退≥15次或缺席≥3次的学生')
    case SelectedRoleEnum.EducationalSupervisor:
      return $t('迟到早退≥10次或缺席≥2次的学生')
    default:
      return ''
  }
}

/**
 * 获取设备违规卡片的提示
 *
 * @param param0 - 参数
 * @param param0.role - 角色
 * @returns 提示
 */
export const getDeviceCardTips = (role: SelectedRoleEnum | undefined) => {
  switch (role) {
    case SelectedRoleEnum.Dean:
    case SelectedRoleEnum.Yuke:
    case SelectedRoleEnum.SchoolLeaders:
      return $t('违规扣分≥3分的学生')
    case SelectedRoleEnum.EducationalSupervisor:
      return $t('违规扣分≥2分的学生')
    default:
      return ''
  }
}

/**
 * 获取考勤列表的提示
 *
 * @param param0 - 参数
 * @param param0.role - 角色
 * @returns 提示
 */
export const getAttendanceListTips = (role: SelectedRoleEnum | undefined) => {
  switch (role) {
    case SelectedRoleEnum.Mentor:
      return $t(
        '默认统计迟到早退≥5次或缺席≥1次的学生，勾选「显示全部」后显示学生从学期初到现在所有的异常考勤数据。'
      )
    case SelectedRoleEnum.Dean:
    case SelectedRoleEnum.Yuke:
    case SelectedRoleEnum.SchoolLeaders:
      return $t(
        '默认统计迟到早退≥15次或缺席≥3次的学生，勾选「显示全部」后显示学生从学期初到现在所有的异常考勤数据。'
      )
    case SelectedRoleEnum.EducationalSupervisor:
      return $t(
        '默认统计迟到早退≥10次或缺席≥2次的学生，勾选「显示全部」后显示学生从学期初到现在所有的异常考勤数据。'
      )
    default:
      return ''
  }
}

/**
 * 获取设备违规列表的提示
 *
 * @param param0 - 参数
 * @param param0.role - 角色
 * @returns 提示
 */
/** 设备违规列表提示项：纯文案或带局部加粗的片段 */
export type DeviceListTipItem = string | ReactNode

export const getDeviceListTips = (
  role: SelectedRoleEnum | undefined
): string | DeviceListTipItem[] => {
  switch (role) {
    case SelectedRoleEnum.Dean:
    case SelectedRoleEnum.Yuke:
    case SelectedRoleEnum.SchoolLeaders:
      return [
        $t('总扣分：本学期学生违规总次数；'),
        createElement(
          Fragment,
          null,
          $t('约谈提醒规则：'),
          createElement('strong', null, $t('筛选的时间段内')),
          $t(
            '，学生违规1次提醒“核实情况 约谈学生”，2次提醒“核实情况 约谈学生和家长”，≥3次提醒“核实情况 约谈学生和家长+处分”；'
          )
        ),
        $t(
          '仅统计电子设备违规扣分≥3分的学生，勾选「显示全部」后显示学生从学期初到现在所有的电子设备违规数据。'
        ),
      ]
    case SelectedRoleEnum.EducationalSupervisor:
      return [
        $t('总扣分：本学期学生违规总次数；'),
        createElement(
          Fragment,
          null,
          $t('约谈提醒规则：'),
          createElement('strong', null, $t('筛选的时间段内')),
          $t(
            '，学生违规1次提醒“核实情况 约谈学生”，2次提醒“核实情况 约谈学生和家长”，≥3次提醒“核实情况 约谈学生和家长+处分”；'
          )
        ),
        $t(
          '仅统计电子设备违规扣分≥2分的学生，勾选「显示全部」后显示学生从学期初到现在所有的电子设备违规数据。'
        ),
      ]
    default:
      return []
  }
}

/**
 * 获取外卖违规列表的提示
 *
 * @param param0 - 参数
 * @param param0.role - 角色
 * @returns 提示
 */
export const getTakeoutListTips = (role: SelectedRoleEnum | undefined) => {
  switch (role) {
    case SelectedRoleEnum.Dean:
    case SelectedRoleEnum.Yuke:
    case SelectedRoleEnum.SchoolLeaders:
      return [
        $t('总扣分：本学期学生违规总次数；'),
        createElement(
          Fragment,
          null,
          $t('约谈提醒规则：'),
          createElement('strong', null, $t('筛选的时间段内')),
          $t(
            '，学生违规1次提醒“核实情况 约谈学生”，2次提醒“核实情况 约谈学生和家长”，≥3次提醒“核实情况 约谈学生和家长+处分”；'
          )
        ),
        $t(
          '仅统计外卖违规扣分≥3分的学生，勾选「显示全部」后显示学生从学期初到现在所有的外卖违规数据。'
        ),
      ]
    case SelectedRoleEnum.EducationalSupervisor:
      return [
        $t('总扣分：本学期学生违规总次数；'),
        createElement(
          Fragment,
          null,
          $t('约谈提醒规则：'),
          createElement('strong', null, $t('筛选的时间段内')),
          $t(
            '，学生违规1次提醒“核实情况 约谈学生”，2次提醒“核实情况 约谈学生和家长”，≥3次提醒“核实情况 约谈学生和家长+处分”；'
          )
        ),
        $t(
          '仅统计外卖违规扣分≥2分的学生，勾选「显示全部」后显示学生从学期初到现在所有的外卖违规数据。'
        ),
      ]
    default:
      return []
  }
}

/**
 * 获取乘梯违规列表的提示
 *
 * @param param0 - 参数
 * @param param0.role - 角色
 * @returns 提示
 */
export const getLiftListTips = (role: SelectedRoleEnum | undefined) => {
  switch (role) {
    case SelectedRoleEnum.Dean:
    case SelectedRoleEnum.Yuke:
    case SelectedRoleEnum.SchoolLeaders:
      return [
        $t('总扣分：本学期学生违规总次数；'),
        createElement(
          Fragment,
          null,
          $t('约谈提醒规则：'),
          createElement('strong', null, $t('筛选的时间段内')),
          $t(
            '，学生违规1次提醒“核实情况 约谈学生”，2次提醒“核实情况 约谈学生和家长”，≥3次提醒“核实情况 约谈学生和家长+处分”；'
          )
        ),
        $t(
          '仅统计乘梯违规扣分≥3分的学生，勾选「显示全部」后显示学生从学期初到现在所有的乘梯违规数据。'
        ),
      ]
    case SelectedRoleEnum.EducationalSupervisor:
      return [
        $t('总扣分：本学期学生违规总次数；'),
        createElement(
          Fragment,
          null,
          $t('约谈提醒规则：'),
          createElement('strong', null, $t('筛选的时间段内')),
          $t(
            '，学生违规1次提醒“核实情况 约谈学生”，2次提醒“核实情况 约谈学生和家长”，≥3次提醒“核实情况 约谈学生和家长+处分”；'
          )
        ),
        $t(
          '仅统计乘梯违规扣分≥2分的学生，勾选「显示全部」后显示学生从学期初到现在所有的乘梯违规数据。'
        ),
      ]
    default:
      return []
  }
}

/**
 * 获取静校管理列表的提示
 *
 * @param param0 - 参数
 * @param param0.role - 角色
 * @returns 提示
 */
export const getStaticManagementListTips = (
  role: SelectedRoleEnum | undefined
) => {
  switch (role) {
    case SelectedRoleEnum.Dean:
    case SelectedRoleEnum.Yuke:
    case SelectedRoleEnum.SchoolLeaders:
      return [
        $t('总扣分：本学期学生违规总次数；'),
        createElement(
          Fragment,
          null,
          $t('约谈提醒规则：'),
          createElement('strong', null, $t('筛选的时间段内')),
          $t(
            '，学生违规1次提醒“核实情况 约谈学生”，2次提醒“核实情况 约谈学生和家长”，≥3次提醒“核实情况 约谈学生和家长+处分”；'
          )
        ),
        $t(
          '仅统计静校管理扣分≥3分的学生，勾选「显示全部」后显示学生从学期初到现在所有的静校管理扣分数据。'
        ),
      ]
    case SelectedRoleEnum.EducationalSupervisor:
      return [
        $t('总扣分：本学期学生违规总次数；'),
        createElement(
          Fragment,
          null,
          $t('约谈提醒规则：'),
          createElement('strong', null, $t('筛选的时间段内')),
          $t(
            '，学生违规1次提醒“核实情况 约谈学生”，2次提醒“核实情况 约谈学生和家长”，≥3次提醒“核实情况 约谈学生和家长+处分”；'
          )
        ),
        $t(
          '仅统计静校管理扣分≥2分的学生，勾选「显示全部」后显示学生从学期初到现在所有的静校管理扣分数据。'
        ),
      ]
    default:
      return []
  }
}
